import { TrendingUp, DollarSign, Activity, Shield, Info } from 'lucide-react';
import type { StockScore } from '../types/api';
import { StockLogo } from './StockLogo';
import { useQueryClient } from '@tanstack/react-query';
import { stockApi } from '../lib/api';
import { useRef } from 'react';

interface StockCardProps {
  stock: StockScore;
  onClick?: () => void;
}

export function StockCard({ stock, onClick }: StockCardProps) {
  const queryClient = useQueryClient();
  const prefetchTimerRef = useRef<number | null>(null);

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Good':
        return '#3ECF8E';
      case 'Average':
        return '#FBBF24';
      case 'Poor':
        return '#EF4444';
      default:
        return '#9E9E9E';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleMouseEnter = () => {
    // Start prefetching after 200ms hover to avoid unnecessary requests
    prefetchTimerRef.current = window.setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: ['stockDetail', stock.symbol],
        queryFn: () => stockApi.getStockDetail(stock.symbol),
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      });
    }, 200);
  };

  const handleMouseLeave = () => {
    // Cancel prefetch if user leaves before 200ms
    if (prefetchTimerRef.current) {
      clearTimeout(prefetchTimerRef.current);
      prefetchTimerRef.current = null;
    }
  };

  return (
    <div
      onClick={onClick}
      className="rounded-xl p-4 sm:p-6 border transition-all duration-200 cursor-pointer group"
      style={{
        backgroundColor: 'rgba(30, 30, 30, 0.6)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}
      onMouseEnter={(e) => {
        handleMouseEnter();
        e.currentTarget.style.backgroundColor = 'rgba(30, 30, 30, 0.8)';
        e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)';
      }}
      onMouseLeave={(e) => {
        handleMouseLeave();
        e.currentTarget.style.backgroundColor = 'rgba(30, 30, 30, 0.6)';
        e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)';
      }}
    >
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
        <StockLogo symbol={stock.symbol} size={56} className="flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="text-xl sm:text-2xl font-bold transition-colors" style={{ color: '#F9F9F9' }}>
              {stock.symbol}
            </h3>
            {stock.current_price && (
              <span className="text-lg sm:text-xl font-bold whitespace-nowrap" style={{ color: '#F9F9F9' }}>
                ${stock.current_price.toFixed(2)}
              </span>
            )}
          </div>
          <p className="text-xs sm:text-sm mt-1 truncate" style={{ color: '#9E9E9E' }}>{stock.name}</p>
          {stock.industry && (
            <span className="inline-block mt-2 px-2 py-1 text-xs rounded" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', color: '#9E9E9E' }}>
              {stock.industry}
            </span>
          )}
        </div>
      </div>

      {/* Score Grid */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-3 sm:mb-4">
        <div className="rounded-lg p-2.5 sm:p-3" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
            <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: '#3ECF8E' }} />
            <span className="text-xs" style={{ color: '#9E9E9E' }}>Value</span>
            <span
              title="Is the stock cheap relative to its earnings and revenue?"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center"
            >
              <Info className="w-3 h-3" style={{ color: '#9E9E9E' }} />
            </span>
          </div>
          <div className="text-xs sm:text-sm font-semibold" style={{ color: getTierColor(stock.value_tier) }}>
            {stock.value_tier}
          </div>
        </div>

        <div className="rounded-lg p-2.5 sm:p-3" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
            <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: '#3ECF8E' }} />
            <span className="text-xs" style={{ color: '#9E9E9E' }}>Growth</span>
            <span
              title="Is the company growing its sales, profits, and future earnings potential?"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center"
            >
              <Info className="w-3 h-3" style={{ color: '#9E9E9E' }} />
            </span>
          </div>
          <div className="text-xs sm:text-sm font-semibold" style={{ color: getTierColor(stock.growth_tier) }}>
            {stock.growth_tier}
          </div>
        </div>

        <div className="rounded-lg p-2.5 sm:p-3" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
            <Activity className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: '#3ECF8E' }} />
            <span className="text-xs" style={{ color: '#9E9E9E' }}>Health</span>
            <span
              title="Is the company profitable and generating strong returns on its capital?"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center"
            >
              <Info className="w-3 h-3" style={{ color: '#9E9E9E' }} />
            </span>
          </div>
          <div className="text-xs sm:text-sm font-semibold" style={{ color: getTierColor(stock.health_tier) }}>
            {stock.health_tier}
          </div>
        </div>

        <div className="rounded-lg p-2.5 sm:p-3" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
          <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
            <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: '#3ECF8E' }} />
            <span className="text-xs" style={{ color: '#9E9E9E' }}>Momentum</span>
            <span
              title="Is the stock's price showing strong upward momentum?"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center"
            >
              <Info className="w-3 h-3" style={{ color: '#9E9E9E' }} />
            </span>
          </div>
          <div className="text-xs sm:text-sm font-semibold" style={{ color: getTierColor(stock.momentum_tier) }}>
            {stock.momentum_tier}
          </div>
        </div>
      </div>

      {/* Tags */}
      {stock.tags && stock.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {stock.tags.map((tag) => {
            const tagColor = tag.color || 'blue';
            const colorMap: Record<string, string> = {
              green: '#3ECF8E',
              blue: '#60A5FA',
              red: '#EF4444',
              yellow: '#FBBF24',
              purple: '#A78BFA',
              pink: '#F472B6',
              orange: '#FB923C',
              cyan: '#22D3EE',
            };
            const color = colorMap[tagColor.toLowerCase()] || '#60A5FA';
            
            return (
              <span
                key={tag.id}
                className="font-medium border"
                style={{
                  fontSize: '13px',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  borderRadius: '6px',
                  backgroundColor: `${color}26`, // 15% opacity
                  color: color,
                  borderColor: `${color}59`, // 35% opacity
                }}
              >
                {tag.name}
              </span>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="pt-3 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        <div className="flex items-center justify-between text-xs" style={{ color: '#9E9E9E' }}>
          <span>Last updated</span>
          <span>{formatDate(stock.last_updated)}</span>
        </div>
      </div>
    </div>
  );
}

